import React, { Suspense, useEffect } from "react";

import { canAccessRoute as pageAccessCanAccessRoute, pageForRoute, switchedOffProduct } from "../Services/pageAccess.js";
import { NotInPlanScreen } from "./NotInPlanScreen.jsx";
import { RouteLoading } from "./shell/RouteLoading.jsx";
import { routeFor } from "./routes.jsx";

// Route access asks the page-access grid the server sent: the same question the server asks
// itself when the page's endpoints are called. The pages registry is the one source of truth.
function canAccessRoute(path, me) {
  return pageAccessCanAccessRoute(me, path);
}

function Redirect({ to, navigate }) {
  useEffect(() => {
    navigate(to);
  }, [navigate, to]);
  return null;
}

// Window titles are set here, from the route table, so every page is identifiable from the tab or window switcher.
// Detail pages overwrite this with record-specific titles once their data loads (e.g. a lead's name from the Sales CRM).
const windowTitle = (route, path) => (typeof route.title === "function" ? route.title(path) : route.title);

export function RouteRenderer(props) {
  const path = (props.route || "").split("?")[0];
  const { data, navigate, loading } = props;
  const me = data?.me;
  const ready = !loading && Boolean(me);
  const route = routeFor(path, me);

  // Hooks always run, in the same order, on every render. They used to sit after an early
  // `return null`, which makes React throw "Rendered more hooks than during the previous
  // render" the moment loading finishes (triage G16, FE-03-01).
  useEffect(() => {
    if (ready) document.title = windowTitle(route, path);
  }, [path, ready]);

  useEffect(() => {
    if (!ready) return;
    if (me?.auditorOnly && !path.startsWith("/audit")) {
      navigate("/audit/");  // an auditor-only account lands on the audit portal
    } else if (!canAccessRoute(path, me)) {
      navigate("/home/");
    }
  }, [path, me, navigate, ready]);

  if (!ready) return null;

  if (!canAccessRoute(path, me)) {
    return null;
  }

  // A product the company has switched off: one shared page that says so, names the
  // product and who can add it (plugin platform). Never shown while every product is on.
  const offProduct = switchedOffProduct(me, pageForRoute(path));
  if (offProduct) {
    return <NotInPlanScreen productCode={offProduct} navigate={navigate} onPluginsChanged={() => props.reload && props.reload(["me"])} />;
  }

  if (route.redirect) return <Redirect to={route.redirect} navigate={navigate} />;
  const { Screen } = route;
  return (
    <Suspense fallback={<RouteLoading />}>
      <Screen {...props} />
    </Suspense>
  );
}
